import type { StyleRule } from '@archipro-design/aria';
import {
    pxToRem,
    SubheaderClassName,
    accordionTitleSlotClassNames,
} from '@archipro-design/aria';
import { svgIconClassName } from '@archipro-design/icons';
import {
    accordionClassName,
    accordionContentClassName,
    accordionTitleClassName,
} from '@fluentui/react-northstar';

export const AccordionTitle: StyleRule = ({ theme }) => ({
    width: `100%`,
    marginBottom: pxToRem(4),
    ...theme.siteVariables.textStyles.Heading.Roman55,
    [`& .${SubheaderClassName}`]: {
        fontSize: `${theme.siteVariables.fontSizes.caption03}`,
        ...theme.siteVariables.textStyles.Heading.Roman55,
    },
    textTransform: 'uppercase',
    [theme.screen.laptop]: {
        fontSize: `${theme.siteVariables.fontSizes.label01} !important`,
        textTransform: 'none',
        paddingRight: pxToRem(20),
        paddingTop: pxToRem(16),
        paddingBottom: pxToRem(16),
        marginBottom: 0,
        minHeight: pxToRem(56),
        [`& .${SubheaderClassName}`]: {
            ...theme.siteVariables.textStyles.Text.Roman55,
        },
    },
});

export const AccordionTitleInner: StyleRule = ({ theme }) => ({
    width: `100%`,
    paddingBottom: pxToRem(12),
    [theme.screen.laptop]: {
        [`& .${SubheaderClassName}`]: {
            letterSpacing: 0,
            lineHeight: theme.siteVariables.lineHeightSmall,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
        },
    },
});

export const AccordionIconWrap: StyleRule = ({ theme }) => ({
    marginLeft: `auto`,
    marginTop: pxToRem(-6),
    marginRight: pxToRem(6),
    fontSize: pxToRem(24),
    [`& .${svgIconClassName}`]: {
        height: '100%',
        display: 'block',
    },
    [theme.screen.laptop]: {
        fontSize: pxToRem(16),
    },
});

export const AccordionContent: StyleRule = ({ theme }) => {
    return {
        '& a': {
            color: `inherit`,
            textDecoration: `underline`,
            fontSize: 'inherit',

            '&:hover': {
                color: `#000`, // to add BLACK/000 in aria
            },
        },
        '& > p, li': {
            marginTop: 0,
            fontSize: pxToRem(16),
            lineHeight: 1.5,
            [theme.screen.laptop]: {
                fontSize: 'inherit',
            },
        },
        '& strong': {
            fontWeight: theme.siteVariables.textStyles.Text.Medium65.fontWeight,
        },

        marginTop: 0,

        [theme.screen.laptop]: {
            fontSize: theme.siteVariables.fontSizes['label03-alt'],
            fontWeight: theme.siteVariables.fontWeightLight,
            letterSpacing: theme.siteVariables.letterSpacingSmall,
            lineHeight: theme.siteVariables.lineHeightSmall,

            [`& > .${accordionContentClassName}`]: {
                marginBottom: pxToRem(60),
                marginTop: pxToRem(32),
            },

            '& a': {
                textDecoration: `underline`,

                '&:hover': {
                    color: `#000`, // to add BLACK/000 in aria
                },
            },

            '& p': {
                marginTop: 'inherit',
                fontSize: 'inherit',
                lineHeight: 'inherit',
            },
        },
    };
};

export const Subheader: StyleRule = () => ({
    '& p': {
        fontSize: pxToRem(24),
    },
});

export const AccordionWrap: StyleRule = ({ theme }) => ({
    [`&.${accordionClassName}`]: {
        borderWidth: pxToRem(1),
        borderColor: theme.siteVariables.colors.grey[125],
        borderBottomWidth: 0,
        whiteSpace: 'normal',
        overflowWrap: 'break-word',
        wordBreak: 'break-all',

        [`& .${accordionContentClassName}`]: {
            marginBottom: pxToRem(18),
            marginTop: pxToRem(8),
            marginRight: pxToRem(-1),
        },

        [`& > .${accordionTitleClassName}`]: {
            paddingRight: 0,
            marginBottom: 0,
            borderTop: `1px solid ${theme.siteVariables.colors.primitive.black}`,

            [`& .${accordionTitleSlotClassNames.contentWrapper}`]: {
                paddingRight: 0,
            },

            [`&:first-child`]: {
                borderTopWidth: '0',
            },

            [`&:last-child`]: {
                borderBottom: `1px solid ${theme.siteVariables.colors.primitive.black}`,
            },
        },

        [theme.screen.laptop]: {
            borderBottomWidth: pxToRem(2),
            borderColor: theme.siteVariables.colors.grey[125],

            marginTop: pxToRem(79),
            [`& > .${accordionTitleClassName}`]: {
                borderWidth: pxToRem(2),
                borderColor: theme.siteVariables.colors.grey[125],
                borderTopWidth: `${pxToRem(2)} !important`,
                marginBottom: 0,
                '&:hover': {
                    cursor: 'pointer',
                    [`& .${SubheaderClassName}`]: {
                        color: theme.siteVariables.colorScheme.default
                            ?.foregroundHover,
                    },
                },
                [`& .${accordionTitleSlotClassNames.contentWrapper}`]: {
                    paddingTop: 0,
                    paddingRight: 0,
                },

                [`&:last-child`]: {
                    borderBottom: `none`,
                },
            },
            [`& .${accordionContentClassName}`]: {
                marginBottom: pxToRem(60),
                marginTop: pxToRem(32),
            },
        },
    },
});
